import React, { useState, useEffect } from 'react';
import api from '../api';
import { norm } from '../utils';
import { useConfirm } from './ConfirmProvider';
import useViewMode from '../hooks/useViewMode';
import ViewToggle from './ViewToggle';
import ExemploBadge from './ExemploBadge';

const FORM_INICIAL = {
  nome: '',
  base_tipo: 'ficha',        // 'ficha' | 'produto'
  base_ficha_id: '',
  base_produto_id: '',
  tem_complemento: false,
  complemento_tipo: 'ficha', // 'ficha' | 'produto'
  complemento_ficha_id: '',
  complemento_produto_id: '',
};

export default function GuiaRecheios() {
  const [recheios, setRecheios] = useState([]);
  const [fichas, setFichas] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_INICIAL);
  const [salvando, setSalvando] = useState(false);
  const confirm = useConfirm();

  useEffect(() => {
    carregarDados();
  }, []);

  async function carregarDados() {
    setLoading(true);
    try {
      const [resRecheios, resFichas, resProdutos] = await Promise.all([
        api.get('/guia/recheios'),
        api.get('/fichas/api/fichas-tecnicas'),
        api.get('/produtos?controla_estoque=true'),
      ]);
      setRecheios(resRecheios.data);
      const cmp = (a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR', { sensitivity: 'base' });
      setFichas([...(resFichas.data?.fichas || [])].sort(cmp));
      setProdutos([...(resProdutos.data || [])].sort(cmp));
    } catch (e) {
      console.error('ERRO carregarDados:', e);
      setErro('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }

  // ── Modal ────────────────────────────────────────────────────────────────────
  function abrirNovo() {
    setForm(FORM_INICIAL);
    setEditando(null);
    setModalAberto(true);
  }

  function abrirEditar(r) {
    setForm({
      nome: r.nome,
      base_tipo: r.base_tipo || 'ficha',
      base_ficha_id: r.base_ficha_id || '',
      base_produto_id: r.base_produto_id || '',
      tem_complemento: r.tem_complemento,
      complemento_tipo: r.complemento_tipo || 'ficha',
      complemento_ficha_id: r.complemento_ficha_id || '',
      complemento_produto_id: r.complemento_produto_id || '',
    });
    setEditando(r.id);
    setModalAberto(true);
  }

  function fecharModal() {
    setModalAberto(false);
    setEditando(null);
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  // ── Salvar ───────────────────────────────────────────────────────────────────
  async function salvar(e) {
    e.preventDefault();
    setSalvando(true);
    setSucesso('');
    setErro('');

    const payload = {
      nome: form.nome,
      base_ficha_id: form.base_tipo === 'ficha' ? Number(form.base_ficha_id) || null : null,
      base_produto_id: form.base_tipo === 'produto' ? Number(form.base_produto_id) || null : null,
      complemento_ficha_id: form.tem_complemento && form.complemento_tipo === 'ficha'
        ? Number(form.complemento_ficha_id) || null : null,
      complemento_produto_id: form.tem_complemento && form.complemento_tipo === 'produto'
        ? Number(form.complemento_produto_id) || null : null,
    };

    try {
      if (editando) {
        await api.put(`/guia/recheios/${editando}`, payload);
        setSucesso('Recheio atualizado com sucesso!');
      } else {
        await api.post('/guia/recheios', payload);
        setSucesso('Recheio cadastrado com sucesso!');
      }
      fecharModal();
      carregarDados();
    } catch (e) {
      setErro(e.response?.data?.erro || 'Erro ao salvar recheio');
    } finally {
      setSalvando(false);
    }
  }

  async function desativar(id) {
    if (!await confirm('Desativar este recheio?')) return;
    try {
      await api.delete(`/guia/recheios/${id}`);
      setSucesso('Recheio desativado.');
      carregarDados();
    } catch {
      setErro('Erro ao desativar recheio');
    }
  }

  // ── Helpers visuais ───────────────────────────────────────────────────────────
  function nomeFicha(id) {
    return fichas.find(f => f.id === id)?.nome || '—';
  }
  function nomeProduto(id) {
    return produtos.find(p => p.id === id)?.nome || '—';
  }

  // ── Filtros ──────────────────────────────────────────────────────────────────
  const [busca, setBusca] = React.useState('');
  const [filtroComplemento, setFiltroComplemento] = React.useState('todos');

  const recheiosFiltrados = recheios.filter(r => {
    const matchBusca = !busca || norm(r.nome).includes(norm(busca));
    const matchCompl = filtroComplemento === 'todos'
      || (filtroComplemento === 'com' && r.tem_complemento)
      || (filtroComplemento === 'sem' && !r.tem_complemento);
    return matchBusca && matchCompl;
  });

  const { mode: vmMode, setMode: setVmMode, effective: vmEffective } = useViewMode('view:recheios');
  const comComplemento = recheios.filter(r => r.tem_complemento).length;
  const semComplemento = recheios.length - comComplemento;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-5xl mx-auto">

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-700">Recheios</h1>
          <p className="text-gray-400 text-sm mt-1">Cadastro de recheios com base e complemento opcional</p>
        </div>
        <button onClick={abrirNovo}
          className="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg font-medium transition-colors text-sm flex items-center gap-2">
          + Novo Recheio
        </button>
      </div>

      {erro && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{erro}</div>}
      {sucesso && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm">{sucesso}</div>}

      {/* Filtros */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="relative">
          <span className="absolute left-3 top-2.5 text-gray-400 text-sm">🔍</span>
          <input type="text" placeholder="Buscar por nome..."
            value={busca} onChange={e => setBusca(e.target.value)}
            className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-300 focus:border-transparent" />
          {busca && (
            <button onClick={() => setBusca('')}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 text-sm">✕</button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <select value={filtroComplemento} onChange={e => setFiltroComplemento(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-300">
            <option value="todos">🍰 Todos os recheios</option>
            <option value="com">✦ Com complemento</option>
            <option value="sem">○ Sem complemento</option>
          </select>
          <ViewToggle mode={vmMode} setMode={setVmMode} />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Carregando...</div>
      ) : recheiosFiltrados.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Nenhum recheio encontrado</p>
          <p className="text-gray-400 text-sm mt-1">{busca ? 'Tente ajustar a busca' : 'Clique em "+ Novo Recheio" para começar'}</p>
        </div>
      ) : (
        <>
          {vmEffective === 'tabela' ? (
            <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 mb-4">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Nome</th>
                    <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Tipo</th>
                    <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Base</th>
                    <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Complemento</th>
                    <th className="text-center px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {recheiosFiltrados.map(r => (
                    <tr key={r.id} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-semibold text-gray-800">{r.nome}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${
                          r.tem_complemento ? 'bg-pink-50 text-pink-600' : 'bg-gray-50 text-gray-500'
                        }`}>
                          {r.tem_complemento ? 'Composto' : 'Simples'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        <span className="mr-1">{r.base_tipo === 'ficha' ? '📋' : '📦'}</span>{r.base_nome || '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {r.tem_complemento ? (
                          <><span className="mr-1">{r.complemento_tipo === 'ficha' ? '📋' : '📦'}</span>{r.complemento_nome}</>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="inline-flex gap-1">
                          <button onClick={() => abrirEditar(r)} title="Editar"
                            className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 inline-flex items-center justify-center text-sm">✏️</button>
                          <button onClick={() => desativar(r.id)} title="Remover"
                            className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 inline-flex items-center justify-center text-sm">🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            {recheiosFiltrados.map(r => (
              <div key={r.id} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col">
                <div className={`h-1 ${r.tem_complemento ? 'bg-pink-400' : 'bg-gray-300'}`} />
                {/* Duas linhas em vez de cinco. Base e complemento eram duas faixas
                    com rotulo em cima do valor, mais uma divisoria e uma faixa so
                    para os dois botoes — quatro alturas de linha para dois nomes.
                    Aqui os dois nomes ficam lado a lado e as acoes dividem a linha
                    com eles; a etiqueta "Composto/Simples" some, porque um segundo
                    nome ao lado do primeiro ja diz que o recheio e composto. */}
                <div className="p-3 flex flex-col gap-1.5 flex-1">
                  <div className="font-semibold text-gray-800 text-[15px] leading-tight truncate min-w-0">{r.nome}<ExemploBadge origem={r.origem} /></div>

                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-[13px] text-gray-600 min-w-0">
                      <span className="inline-flex items-center gap-1 min-w-0 max-w-full">
                        <span className="text-[11px] shrink-0">{r.base_tipo === 'ficha' ? '📋' : '📦'}</span>
                        <span className="truncate">{r.base_nome || '—'}</span>
                      </span>
                      {r.tem_complemento && (
                        <span className="inline-flex items-center gap-1 min-w-0 max-w-full">
                          <span className="text-gray-300">+</span>
                          <span className="text-[11px] shrink-0">{r.complemento_tipo === 'ficha' ? '📋' : '📦'}</span>
                          <span className="truncate">{r.complemento_nome}</span>
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <button onClick={() => abrirEditar(r)} title="Editar"
                        className="w-9 h-9 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 inline-flex items-center justify-center transition-colors text-[15px] leading-none">✏️</button>
                      <button onClick={() => desativar(r.id)} title="Remover"
                        className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 inline-flex items-center justify-center transition-colors text-[15px] leading-none">🗑️</button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}

          {/* Rodapé */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex flex-col md:flex-row justify-between items-center gap-3">
              <span className="font-medium text-gray-700 text-sm">
                Total: {recheiosFiltrados.length} {recheiosFiltrados.length === 1 ? 'recheio' : 'recheios'}
              </span>
              <div className="flex gap-3">
                <span className="bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-sm font-medium">
                  ✦ {comComplemento} com complemento
                </span>
                <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm font-medium">
                  ○ {semComplemento} simples
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal */}
      {modalAberto && (
        /* Sem teto de altura o modal ficava maior que a tela e, centralizado,
            escorregava metade para cima: o titulo sumia atras do cabecalho do app
            e nao havia como rolar ate ele. Com max-h + rolagem interna (e folha
            no celular, como os demais modais) o topo sempre aparece. */
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4 modal-overlay-mobile" style={{ background: 'rgba(61,44,44,0.3)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto modal-content-sheet">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-800">
                {editando ? 'Editar Recheio' : 'Novo Recheio'}
              </h2>
              <button onClick={fecharModal} className="text-gray-400 hover:text-gray-600 text-xl font-bold">
                ×
              </button>
            </div>

            <form onSubmit={salvar} className="px-6 py-5 space-y-5">

              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input
                  name="nome"
                  value={form.nome}
                  onChange={handleChange}
                  placeholder="ex: Brigadeiro, Frutas Vermelhas"
                  required
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                />
              </div>

              {/* Base */}
              <section className="bg-gray-50 rounded-xl p-4 space-y-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Base *
                </h3>

                <div className="flex gap-3">
                  {['ficha', 'produto'].map(tipo => (
                    <label key={tipo} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="base_tipo"
                        value={tipo}
                        checked={form.base_tipo === tipo}
                        onChange={handleChange}
                        className="accent-pink-600"
                      />
                      <span className="text-sm text-gray-700">
                        {tipo === 'ficha' ? '📋 Ficha técnica' : '📦 Produto (insumo direto)'}
                      </span>
                    </label>
                  ))}
                </div>

                {form.base_tipo === 'ficha' ? (
                  <select
                    name="base_ficha_id"
                    value={form.base_ficha_id}
                    onChange={handleChange}
                    required
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                  >
                    <option value="">Selecione a ficha técnica...</option>
                    {fichas.map(f => (
                      <option key={f.id} value={f.id}>{f.nome}</option>
                    ))}
                  </select>
                ) : (
                  <select
                    name="base_produto_id"
                    value={form.base_produto_id}
                    onChange={handleChange}
                    required
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                  >
                    <option value="">Selecione o produto...</option>
                    {produtos.map(p => (
                      <option key={p.id} value={p.id}>{p.nome}</option>
                    ))}
                  </select>
                )}
              </section>

              {/* Complemento */}
              <section className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="tem_complemento"
                    checked={form.tem_complemento}
                    onChange={handleChange}
                    className="accent-pink-600"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Tem complemento? (ex: Nutella, Geleia)
                  </span>
                </label>

                {form.tem_complemento && (
                  <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Complemento
                    </h3>

                    <div className="flex gap-3">
                      {['ficha', 'produto'].map(tipo => (
                        <label key={tipo} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="complemento_tipo"
                            value={tipo}
                            checked={form.complemento_tipo === tipo}
                            onChange={handleChange}
                            className="accent-pink-600"
                          />
                          <span className="text-sm text-gray-700">
                            {tipo === 'ficha' ? '📋 Ficha técnica' : '📦 Produto'}
                          </span>
                        </label>
                      ))}
                    </div>

                    {form.complemento_tipo === 'ficha' ? (
                      <select
                        name="complemento_ficha_id"
                        value={form.complemento_ficha_id}
                        onChange={handleChange}
                        required
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                      >
                        <option value="">Selecione a ficha técnica...</option>
                        {fichas.map(f => (
                          <option key={f.id} value={f.id}>{f.nome}</option>
                        ))}
                      </select>
                    ) : (
                      <select
                        name="complemento_produto_id"
                        value={form.complemento_produto_id}
                        onChange={handleChange}
                        required
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                      >
                        <option value="">Selecione o produto...</option>
                        {produtos.map(p => (
                          <option key={p.id} value={p.id}>{p.nome}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </section>

              <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={fecharModal}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={salvando}
                  className="bg-pink-600 hover:bg-pink-700 disabled:opacity-60 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Cadastrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}